import {View, Text, StyleSheet} from 'react-native';
import {useTranslation} from 'react-i18next';
import {Ionicons} from '@expo/vector-icons';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {ThemeType} from "@/theming/baseTheme";
import type {TaxBreakdown} from '@/data/app-data-interfaces';

interface PriceInfo {
    price: number;
    currency: string;
    isFree: boolean;
}

interface OfferPriceDisplayProps {
    priceInfo: PriceInfo;
    isPurchased: boolean;
    taxBreakdown?: TaxBreakdown | null;
    paymentDenied?: boolean;
    hideTaxes?: boolean;
}

export const OfferPriceDisplay = ({priceInfo, isPurchased, taxBreakdown, paymentDenied, hideTaxes}: OfferPriceDisplayProps) => {
    const {t, i18n} = useTranslation();
    const {theme} = useCurrentTheme();
    const styles = createStyles(theme);

    const formatPrice = (price: number, currency: string) => {
        return new Intl.NumberFormat(i18n.language, {
            style: 'currency',
            currency: currency.toUpperCase(),
        }).format(price);
    };

    if (isPurchased) {
        return (
            <View style={styles.purchasedContainer}>
                <View style={styles.purchasedStatus}>
                    <Ionicons name="checkmark-circle" size={24} color={theme.colors.success || theme.colors.primary} />
                    <Text style={styles.purchasedStatusText}>{t('offers.purchased')}</Text>
                </View>
                <Text style={styles.purchasedSubtext}>{t('offers.youOwnThis')}</Text>
            </View>
        );
    }

    const finalPrice = taxBreakdown?.grossAmount || priceInfo.price;

    return (
        <View style={styles.priceContainer}>
            <View style={styles.priceValueContainer}>
                {priceInfo.isFree ? (
                    <Text style={styles.freeText}>{t('offers.free')}</Text>
                ) : (
                    <View style={styles.paidPriceContainer}>
                        <Text style={styles.currencySymbol}>
                            {priceInfo.currency === 'EUR' ? '€' : '$'}
                        </Text>
                        <Text style={styles.priceAmount}>
                            {Math.floor(finalPrice)}
                        </Text>
                        {priceInfo.price % 1 !== 0 && (
                            <Text style={styles.priceCents}>
                                {t('common.decimalSeparator')}{((finalPrice % 1) * 100).toFixed(0).padStart(2, '0')}
                            </Text>
                        )}
                    </View>
                )}
            </View>

            {taxBreakdown && !hideTaxes && !priceInfo.isFree && (
                <View style={styles.taxBreakdownContainer}>
                    <View style={styles.taxRow}>
                        <Text style={styles.taxLabel}>{t('offers.pricing.netAmount')}</Text>
                        <Text style={styles.taxValue}>{formatPrice(taxBreakdown.netAmount, taxBreakdown.currency)}</Text>
                    </View>
                    <View style={styles.taxRow}>
                        <Text style={styles.taxLabel}>
                            {taxBreakdown.taxName} ({taxBreakdown.taxRate.toFixed(2)}%)
                        </Text>
                        <Text style={styles.taxValue}>{formatPrice(taxBreakdown.taxAmount, taxBreakdown.currency)}</Text>
                    </View>
                </View>
            )}
            {paymentDenied && (
                <View style={styles.paymentDeniedContainer}>
                    <Ionicons name="alert-circle" size={24} color={theme.colors.error} />
                    <View style={styles.paymentDeniedTextContainer}>
                        <Text style={styles.paymentDeniedTitle}>{t('offers.paymentDenied.title')}</Text>
                        <Text style={styles.paymentDeniedText}>{t('offers.paymentDenied.description')}</Text>
                    </View>
                </View>
            )}
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    priceContainer: {
        alignItems: 'center',
        width: '100%',
    },
    priceValueContainer: {
        marginVertical: 8,
    },
    paidPriceContainer: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'center',
    },
    currencySymbol: {
        fontSize: 24,
        fontWeight: '400',
        color: theme.colors.textSecondary,
        marginTop: 8,
        marginRight: 4,
    },
    priceAmount: {
        fontSize: 48,
        fontWeight: '700',
        color: theme.colors.text,
        lineHeight: 48,
    },
    priceCents: {
        fontSize: 24,
        fontWeight: '600',
        color: theme.colors.textSecondary,
        marginTop: 8,
        marginLeft: 2,
    },
    freeText: {
        fontSize: theme.fontSize.xxl,
        fontWeight: '700',
        color: theme.colors.success || theme.colors.primary,
    },
    purchasedContainer: {
        alignItems: 'center',
    },
    purchasedStatus: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.sm,
    },
    purchasedStatusText: {
        fontSize: 18,
        color: theme.colors.success || theme.colors.primary,
        fontWeight: '600',
    },
    purchasedSubtext: {
        fontSize: 14,
        color: theme.colors.textSecondary,
        marginTop: 12,
    },
    paymentDeniedContainer: {
        flexDirection: 'row',
        backgroundColor: '#FEF2F2',
        borderWidth: 1,
        borderColor: '#FEE2E2',
        borderRadius: 12,
        padding: 16,
        marginTop: 16,
        width: '100%',
    },
    paymentDeniedTextContainer: {
        flex: 1,
        marginLeft: 12,
    },
    paymentDeniedTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: '#991B1B',
        marginBottom: 4,
    },
    paymentDeniedText: {
        fontSize: 14,
        color: '#B91C1C',
    },
    taxBreakdownContainer: {
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
        marginTop: 16,
        paddingTop: 12,
        width: '100%',
    },
    taxRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 8,
    },
    taxLabel: {
        fontSize: 14,
        color: theme.colors.textSecondary,
    },
    taxValue: {
        fontSize: 14,
        fontWeight: '600',
        color: theme.colors.text,
    },
});
